/**
 * FieldLockBadge — inline "Jane is editing this field" chip.
 *
 * Render next to any Workbench input that is gated by a field lock. It
 * expects the live lock descriptor (produced by ``useFieldLock`` or pulled
 * from the lock snapshot). If ``lock`` is falsy the component returns null
 * so callers can unconditionally mount it.
 *
 * Workspace admins get a discreet "break" link that force-releases the
 * lock via ``releaseFieldLock(field, { force: true })``. We intentionally
 * keep the affordance visual-noisy only when ``canForceRelease`` is true to
 * avoid tempting ordinary users into stomping.
 */
import React from 'react';

function displayFor(lock) {
  const email = (lock?.holderEmail || '').trim();
  if (email) return email.split('@')[0];
  const sub = (lock?.holderSub || '').trim();
  return sub ? `${sub.slice(0, 8)}…` : 'someone';
}

export default function FieldLockBadge({
  lock,
  isSelf = false,
  canForceRelease = false,
  onForceRelease,
  className = '',
}) {
  if (!lock || !lock.field) return null;

  // Self-held locks render as an assurance pill, not a blocker — helps the
  // current editor see their own lock status without alarm.
  if (isSelf) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 ${className}`}
        title="You hold this field's edit lock"
      >
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 12l5 5L20 7" />
        </svg>
        You're editing
      </span>
    );
  }

  const who = displayFor(lock);

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200 ${className}`}
      title={`Locked by ${who} until ${
        lock.expiresAt ? new Date(lock.expiresAt * 1000).toLocaleTimeString() : 'soon'
      }`}
    >
      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-7a2 2 0 00-2-2H6a2 2 0 00-2 2v7a2 2 0 002 2zm10-11V7a4 4 0 10-8 0v4h8z" />
      </svg>
      <span className="truncate max-w-[120px]">{who} is editing</span>
      {canForceRelease && typeof onForceRelease === 'function' ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onForceRelease(lock);
          }}
          className="ml-1 rounded px-1 text-[10px] font-semibold uppercase text-amber-900/70 hover:bg-amber-100 hover:text-amber-900"
          title="Force-release this lock (admin only)"
        >
          break
        </button>
      ) : null}
    </span>
  );
}
